import { useState, useEffect } from 'react';
import {
  MapPin,
  Plus,
  Trash2,
  Edit2,
  X,
  Save,
  Navigation,
  Flag,
  Home,
  AlertTriangle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useApp } from '../context/AppContext';
import {
  getAllReunionPoints,
  addReunionPoint,
  updateReunionPoint,
  deleteReunionPoint,
} from '../db/database';
import type { ReunionPoint } from '../types';

// Fix for default marker icon
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const createIcon = (color: string) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 3px 10px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg style="transform: rotate(45deg); width: 16px; height: 16px; color: white;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const icons = {
  primary: createIcon('#dc3329'),
  secondary: createIcon('#f59e0b'),
  emergency: createIcon('#22c55e'),
};

function LocationMarker({ onLocationSelect }: { onLocationSelect: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function RecenterButton({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();

  return (
    <button
      onClick={() => map.setView([lat, lng], 13)}
      className="absolute bottom-4 right-4 z-[1000] bg-gray-800 text-white p-3 rounded-lg shadow-lg hover:bg-gray-700 transition-colors"
      title="Center on your location"
    >
      <Navigation className="w-5 h-5" />
    </button>
  );
}

export function ReunionPoints() {
  const { addNotification } = useApp();
  const [points, setPoints] = useState<ReunionPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectingLocation, setSelectingLocation] = useState(false);
  const [editingPoint, setEditingPoint] = useState<ReunionPoint | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: 0,
    longitude: 0,
    type: 'primary' as ReunionPoint['type'],
    address: '',
    contactPerson: '',
    notes: '',
  });

  useEffect(() => {
    loadPoints();
    getUserLocation();
  }, []);

  function getUserLocation() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Default to a central US location
          setUserLocation({ lat: 39.8283, lng: -98.5795 });
        }
      );
    } else {
      setUserLocation({ lat: 39.8283, lng: -98.5795 });
    }
  }

  async function loadPoints() {
    try {
      const data = await getAllReunionPoints();
      setPoints(data);
    } catch (error) {
      console.error('Failed to load points:', error);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      name: '',
      description: '',
      latitude: userLocation?.lat || 0,
      longitude: userLocation?.lng || 0,
      type: 'primary',
      address: '',
      contactPerson: '',
      notes: '',
    });
    setEditingPoint(null);
  }

  function openAddModal() {
    resetForm();
    setShowModal(true);
  }

  function openEditModal(point: ReunionPoint) {
    setEditingPoint(point);
    setFormData({
      name: point.name,
      description: point.description || '',
      latitude: point.latitude,
      longitude: point.longitude,
      type: point.type,
      address: point.address || '',
      contactPerson: point.contactPerson || '',
      notes: point.notes || '',
    });
    setShowModal(true);
  }

  function handleLocationSelect(lat: number, lng: number) {
    setFormData({ ...formData, latitude: lat, longitude: lng });
    setSelectingLocation(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      if (editingPoint) {
        await updateReunionPoint(editingPoint.id, formData);
        addNotification('success', 'Updated', 'Reunion point updated');
      } else {
        await addReunionPoint(formData);
        addNotification('success', 'Added', 'Reunion point added');
      }
      setShowModal(false);
      resetForm();
      loadPoints();
    } catch (error) {
      console.error('Failed to save point:', error);
      addNotification('error', 'Error', 'Failed to save reunion point');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this reunion point?')) return;
    await deleteReunionPoint(id);
    loadPoints();
  }

  const getTypeIcon = (type: ReunionPoint['type']) => {
    switch (type) {
      case 'primary':
        return <Home className="w-4 h-4" />;
      case 'secondary':
        return <Flag className="w-4 h-4" />;
      case 'emergency':
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: ReunionPoint['type']) => {
    switch (type) {
      case 'primary':
        return 'text-emergency-400';
      case 'secondary':
        return 'text-warning-400';
      case 'emergency':
        return 'text-safe-400';
    }
  };

  if (!userLocation) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading map...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MapPin className="w-6 h-6 text-emergency-400" />
            Family Reunion Points
          </h1>
          <p className="text-gray-400 mt-1">Mark safe meeting locations for your family</p>
        </div>
        <button onClick={openAddModal} className="btn-emergency">
          <Plus className="w-4 h-4" />
          Add Point
        </button>
      </div>

      {/* Map */}
      <div className="card overflow-hidden">
        <div className="h-[400px] lg:h-[500px] relative">
          <MapContainer
            center={[userLocation.lat, userLocation.lng]}
            zoom={13}
            className="h-full w-full"
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {selectingLocation && (
              <LocationMarker onLocationSelect={handleLocationSelect} />
            )}

            {points.map((point) => (
              <Marker
                key={point.id}
                position={[point.latitude, point.longitude]}
                icon={icons[point.type]}
              >
                <Popup>
                  <div className="p-1">
                    <h3 className="font-semibold text-gray-900">{point.name}</h3>
                    <p className="text-sm text-gray-600 capitalize">{point.type} Point</p>
                    {point.description && (
                      <p className="text-sm text-gray-700 mt-1">{point.description}</p>
                    )}
                    {point.address && (
                      <p className="text-xs text-gray-500 mt-1">{point.address}</p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            <RecenterButton lat={userLocation.lat} lng={userLocation.lng} />
          </MapContainer>

          {selectingLocation && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-warning-600 text-black px-4 py-2 rounded-lg shadow-lg">
              Click on the map to select location
              <button
                onClick={() => setSelectingLocation(false)}
                className="ml-2 underline"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Points List */}
      <div>
        <h2 className="section-title">
          <Flag className="w-5 h-5 text-warning-400" />
          Saved Locations
        </h2>
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="card p-4 animate-pulse">
                <div className="h-4 bg-gray-700 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : points.length === 0 ? (
          <div className="card p-6 text-center">
            <MapPin className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No reunion points added yet</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {points.map((point) => (
              <motion.div
                key={point.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`card p-4 ${
                  point.type === 'primary'
                    ? 'border-l-4 border-l-emergency-500'
                    : point.type === 'secondary'
                    ? 'border-l-4 border-l-warning-500'
                    : 'border-l-4 border-l-safe-500'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={getTypeColor(point.type)}>
                        {getTypeIcon(point.type)}
                      </span>
                      <h3 className="font-semibold text-white truncate">{point.name}</h3>
                      <span className={`badge ${
                        point.type === 'primary'
                          ? 'badge-emergency'
                          : point.type === 'secondary'
                          ? 'badge-warning'
                          : 'badge-safe'
                      }`}>
                        {point.type}
                      </span>
                    </div>
                    {point.description && (
                      <p className="text-sm text-gray-400 mb-1">{point.description}</p>
                    )}
                    {point.address && (
                      <p className="text-xs text-gray-500">{point.address}</p>
                    )}
                    {point.contactPerson && (
                      <p className="text-xs text-gray-500 mt-1">
                        Contact: {point.contactPerson}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEditModal(point)}
                      className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(point.id)}
                      className="p-2 rounded-lg hover:bg-emergency-900/50 text-gray-400 hover:text-emergency-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">
                  {editingPoint ? 'Edit Reunion Point' : 'Add Reunion Point'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-700 text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="label">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    placeholder="Home, Park, School..."
                  />
                </div>

                <div>
                  <label className="label">Type *</label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as ReunionPoint['type'] })}
                    className="input"
                  >
                    <option value="primary">Primary - First meeting place</option>
                    <option value="secondary">Secondary - Backup location</option>
                    <option value="emergency">Emergency - Last resort</option>
                  </select>
                </div>

                <div>
                  <label className="label">Location *</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="any"
                      required
                      value={formData.latitude || ''}
                      onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
                      className="input"
                      placeholder="Latitude"
                    />
                    <input
                      type="number"
                      step="any"
                      required
                      value={formData.longitude || ''}
                      onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
                      className="input"
                      placeholder="Longitude"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setSelectingLocation(true);
                    }}
                    className="btn-secondary w-full mt-2"
                  >
                    <MapPin className="w-4 h-4" />
                    Select on Map
                  </button>
                </div>

                <div>
                  <label className="label">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="input"
                    placeholder="123 Main St, City, State"
                  />
                </div>

                <div>
                  <label className="label">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input"
                    placeholder="Near the old oak tree..."
                  />
                </div>

                <div>
                  <label className="label">Contact Person</label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    className="input"
                    placeholder="Who to look for"
                  />
                </div>

                <div>
                  <label className="label">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input min-h-[80px] resize-none"
                    placeholder="Additional information..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-emergency flex-1">
                    <Save className="w-4 h-4" />
                    {editingPoint ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
